import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BookIcon,
  CalendarIcon,
  ClockIcon,
  DocMagnifyGlassIcon,
  DollarIcon,
  FileIcon,
  GraphIcon,
  PenPaperIcon,
  XIcon,
} from "assets/icons";
import { Button } from "components/atoms";
import { Breadcrumbs } from "components/molecules";
import { useBreadcrumb, useCurrency, useCompare } from "contexts";
import { formatDateToMonthYear, formatFees } from "services";
import "./CompareCourseLayout.scss";

const courseInfo = [
  {
    label: "Course Code",
    icon: <FileIcon />,
    getValue: (course: any) => course.courseCode,
  },
  {
    label: "Level",
    icon: <GraphIcon />,
    getValue: (course: any) => course.level,
  },
  {
    label: "Field of Study",
    icon: <DocMagnifyGlassIcon />,
    getValue: (course: any) =>
      Array.isArray(course.fieldOfStudy)
        ? course.fieldOfStudy.join(", ")
        : course.fieldOfStudy,
  },
  {
    label: "Mode of Study",
    icon: <BookIcon />,
    getValue: (course: any) => course.modeOfStudy,
  },
  {
    label: "Program Type",
    icon: <PenPaperIcon />,
    getValue: (course: any) => course.programType,
  },
  {
    label: "Start Date",
    icon: <CalendarIcon />,
    getValue: (course: any) => {
      if (!course.startDate) return "N/A";

      const formatted = formatDateToMonthYear(course.startDate);
      return formatted === "Invalid Date" ? "N/A" : formatted;
    },
  },
  {
    label: "Duration",
    icon: <ClockIcon />,
    getValue: (course: any) => course.durationDay,
  },
  {
    label: "Estimated Total Fee",
    icon: <DollarIcon />,
    getValue: (course: any, currencySymbol: string, conversionRate: number) =>
      formatFees(
        course.estimatedTotalCourseFee,
        currencySymbol,
        conversionRate,
      ),
  },
];

const GRID_ROW_SPAN = courseInfo.length + 1;

const CompareCourseLayout: React.FC = () => {
  const location = useLocation();
  const { compareCourses, removeFromCompare } = useCompare();
  const { currencySymbol, conversionRate } = useCurrency();
  const { updateBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    const parentPath = location.state?.from || "/search";
    const parentLabel = "Search";

    updateBreadcrumbs([
      { path: parentPath, label: parentLabel },
      {
        path: `/compare`,
        label: "Compare Course",
      },
    ]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  return (
    <div className="compareCourse-container">
      <div className="container">
        <Breadcrumbs />
        <header className="compare-header">
          <h1>Course Comparison</h1>
          <p>Compare up to 3 courses side by side</p>
        </header>

        {compareCourses.length === 0 ? (
          <div className="no-courses-message">
            No courses selected for comparison. Add courses to compare them.
          </div>
        ) : (
          <div
            className="comparison-grid"
            style={
              {
                "--course-count": compareCourses.length,
              } as React.CSSProperties
            }
          >
            <div className="grid-cell details-header">
              <div className="details-header__text">
                <span className="details-header__title">Course Details</span>
                <span className="details-header__subtitle">
                  Review and compare key information across courses.
                </span>
              </div>
            </div>

            {courseInfo.map(({ label, icon }, index) => (
              <div
                key={label}
                className="grid-cell row-label-cell"
                style={{ gridRow: index + 2 }}
              >
                <span className="row-label">
                  <span className="row-label__icon">{icon}</span>
                  {label}
                </span>
              </div>
            ))}

            {compareCourses.map((course, courseIndex) => (
              <div
                key={course.id}
                className="course-block"
                style={{
                  gridColumn: courseIndex + 2,
                  gridRow: `1 / span ${GRID_ROW_SPAN}`,
                }}
              >
                <div className="course-block__header">
                  <Link
                    to={`/course/${course.id}`}
                    state={{ from: "/compare" }}
                    className="course-block__name"
                  >
                    {course.courseName}
                  </Link>
                  <Button
                    type="button"
                    text={<XIcon />}
                    variant="borderless"
                    onClick={() => removeFromCompare(course.id)}
                    className="remove-button"
                    aria-label="Remove course"
                  />
                </div>

                {courseInfo.map(({ label, icon, getValue }) => (
                  <div
                    key={label}
                    className={`course-block__cell${
                      label === "Estimated Total Fee" ? " fee-value" : ""
                    }`}
                  >
                    <span className="row-label course-block__mobile-label">
                      <span className="row-label__icon">{icon}</span>
                      {label}
                    </span>
                    <span className="course-block__value">
                      {getValue(course, currencySymbol, conversionRate)}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompareCourseLayout;
